import { useCallback, useEffect, useRef } from 'react';

function prepareInlinePlayback(element) {
  // Set both properties and attributes before play(), including Safari's
  // default-muted state. JSX's muted property alone does not set that default.
  element.defaultMuted = true;
  element.muted = true;
  element.playsInline = true;
  element.setAttribute('muted', '');
  element.setAttribute('playsinline', '');
  element.setAttribute('webkit-playsinline', '');
}

export default function useMotionAutoplay(video, { enabled, mediaKey, active: screenActive = true }) {
  const startup = useRef(null);
  const completedFor = useRef(null);
  const bindVideo = useCallback((element) => {
    video.current = element;
    if (element) prepareInlinePlayback(element);
  }, [video]);
  const cancelAutoplay = useCallback(() => startup.current?.stop(), []);

  useEffect(() => {
    const element = video.current;
    if (!element) return undefined;
    if (!screenActive) {
      // Native navigation can hide a still-mounted WebView without changing
      // document.visibilityState. Preload bytes, but stop background playback.
      completedFor.current = null;
      element.pause();
      return undefined;
    }
    let active = true;
    let finished = completedFor.current === mediaKey || !element.paused;
    let pending = false;
    let readyWhilePending = false;
    let attempts = 0;
    const stop = () => { finished = true; readyWhilePending = false; completedFor.current = mediaKey; };
    if (finished) completedFor.current = mediaKey;
    const controller = { stop };
    startup.current = controller;
    const attempt = () => {
      if (!enabled || !active || finished || attempts >= 6 || document.visibilityState === 'hidden' || !element.paused) return;
      if (pending) { readyWhilePending = true; return; }
      attempts += 1;
      pending = true;
      readyWhilePending = false;
      prepareInlinePlayback(element);
      const rejected = () => {
        pending = false;
        // If readiness changed while a rejected play() was pending, retry once
        // for that event. There is no timer loop or background polling.
        if (active && readyWhilePending) { readyWhilePending = false; attempt(); }
      };
      try {
        const playback = element.play();
        if (playback?.then) playback.then(() => { pending = false; if (active) stop(); }, rejected);
        else pending = false; // Older engines report success through `playing`.
      } catch (_) { rejected(); }
    };
    const readyEvents = ['loadedmetadata', 'loadeddata', 'canplay'];
    // Remember manual playback even in the desktop layout, so switching to
    // the phone layout cannot undo the viewer's pause.
    element.addEventListener('playing', stop);
    element.addEventListener('pause', stop);
    if (enabled) {
      readyEvents.forEach((event) => element.addEventListener(event, attempt));
      document.addEventListener('visibilitychange', attempt);
      window.addEventListener('pageshow', attempt);
    }
    attempt();
    return () => {
      active = false;
      finished = true; readyWhilePending = false;
      readyEvents.forEach((event) => element.removeEventListener(event, attempt));
      element.removeEventListener('playing', stop);
      element.removeEventListener('pause', stop);
      document.removeEventListener('visibilitychange', attempt);
      window.removeEventListener('pageshow', attempt);
      if (startup.current === controller) startup.current = null;
    };
  }, [enabled, mediaKey, screenActive, video]);
  return { bindVideo, cancelAutoplay };
}
